import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useGetCategoriesQuery } from '~domains/orders/entities/product';

import { plural } from '~shared/lib/plural';
import { InputNumber } from '~shared/ui/input-number';

import { Button } from '@breezeventures/ui-kit/button';
import { Checkbox } from '@breezeventures/ui-kit/checkbox/rhf';
import { Select } from '@breezeventures/ui-kit/select/rhf';
import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { InputMoney } from '@breezeventures/web-ph-components/input-money';
import {
  combineValidators,
  validators,
} from '@breezeventures/web-ph-validators';

import { latinTextValidator } from '../../lib/latin-text-validator';
import type { ItemFormValue } from '../order-form';

type Props = {
  onDone: (item: ItemFormValue) => void;
  allowsUsedItems: boolean;
};

export const ItemForm = ({ onDone, allowsUsedItems }: Props) => {
  const { control, handleSubmit, watch } = useForm<ItemFormValue>({
    mode: 'all',
    defaultValues: { quantity: 1 },
  });
  const { data: categories } = useGetCategoriesQuery();

  const categoriesOptions = useMemo(
    () =>
      categories
        ?.map((category) => ({
          title: category.displayName,
          value: category.code,
        }))
        .concat({ title: 'Shipping', value: 'SHIPPING' }),
    [categories]
  );

  const categoryTitleMap = useMemo(
    () =>
      Object.fromEntries(
        categoriesOptions?.map(({ value, title }) => [value, title]) ?? []
      ),
    [categoriesOptions]
  );

  const quantity = watch('quantity');

  return (
    <form onSubmit={handleSubmit(onDone)} className="flex flex-col gap-4">
      <Controller
        name="category"
        rules={{ required: 'Please, select an option' }}
        control={control}
        render={(rp) => (
          <Select
            {...rp}
            label="Product type"
            labelPosition="inner"
            renderValue={(value) => categoryTitleMap[value] ?? value}
          >
            {categoriesOptions?.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.title}
                <Select.OptionIndicator />
              </Select.Option>
            ))}
          </Select>
        )}
      />
      <Controller
        name="name"
        control={control}
        rules={{
          validate: combineValidators(
            validators.required({ text: 'Please, enter the name' }),
            validators.maxLength({ maxLength: 300 }),
            latinTextValidator
          ),
        }}
        render={(rp) => (
          <TextField
            {...rp}
            label="Name of the product"
            labelPosition="inner"
          />
        )}
      />
      <Controller
        name="price"
        control={control}
        rules={{
          validate: combineValidators(
            validators.required({ text: 'Please, enter the price' }),
            (v) => Number(v) > 0 || 'Price must be greater than 0',
            (v) => Number(v) <= 999999 || 'Price must not exceed ₱999,999'
          ),
        }}
        render={(rp) => (
          <InputMoney {...rp} label="Price" labelPosition="inner" />
        )}
      />
      <Controller
        name="quantity"
        control={control}
        rules={{
          validate: combineValidators(
            validators.required({ text: 'Please, enter the number' }),
            (v) =>
              (Number.isInteger(Number(v)) && Number(v) >= 1) ||
              'Please enter a valid whole number'
          ),
        }}
        render={(rp) => (
          <InputNumber {...rp} label="Number of items" labelPosition="inner" />
        )}
      />
      {allowsUsedItems && (
        <Controller
          name="isUsed"
          control={control}
          render={(rp) => (
            <Checkbox {...rp}>
              {plural(+quantity, 'Used\u00A0item', 'Used\u00A0items')}
            </Checkbox>
          )}
        />
      )}
      <Button type="submit" size="l" width="full">
        Done
      </Button>
    </form>
  );
};
